import type { DeepPartial } from "../../utils/DeepPartial.js";
import { createRecordFromEntries } from "../../utils/enum/createRecordFromEntries.js";
import { formatPrice } from "../../utils/formatPrice.js";
import { translate } from "../../utils/translate.js";
import type { Currency } from "../Currency.js";
import type { Locale, LocalizedString } from "../Locale.js";
import type { Order } from "../Order.js";
import { OrderDiscountType } from "../OrderDiscountType.js";
import type { OrderSummaryRow } from "../OrderSummaryRow.js";
import { deliveryTypeNameMap } from "../names/deliveryTypeNameMap.js";
import { orderDiscountTypeNameMap } from "../names/orderDiscountTypeNameMap.js";
import translations from "./getOrderSummaryRows.translations.json" with { type: "json" };

function getTranslate<Translations extends Record<string, LocalizedString>>(
	translations: Translations,
	locale: Locale,
) {
	return (key: keyof Translations, variables: Record<string, string | number> = {}): string => {
		return translate(translations, key, locale, variables);
	};
}

const discounts = Object.values(OrderDiscountType);
export function getOrderSummaryRows(
	locale: Locale,
	currency: Currency,
	order: DeepPartial<Pick<Order, "deliveryType" | "deliveryAddress" | "prices" | "couponCode">> | undefined,
	showDelivery = true,
): OrderSummaryRow[] {
	const translate = getTranslate(translations, locale);
	const roundingAdjustment = order?.prices?.roundingAdjustment ?? 0;
	const discountsByType = createRecordFromEntries(
		OrderDiscountType,
		discounts.map((type) => [
			type,
			(order?.prices?.appliedDiscounts?.discountsByTaxRate ?? []).reduce(
				(sum, price) => sum + (price?.type === type ? (price.withTax ?? 0) : 0),
				0,
			),
		]),
	);
	const totalDiscount = Object.values(discountsByType).reduce((a, b) => a + b, 0);
	const deliveryDiscount = order?.prices?.appliedDiscounts?.deliveryPrice ?? 0;
	const totalPriceWithTax = (order?.prices?.totalPrice?.withTax ?? 0) - roundingAdjustment;
	const totalPriceWithoutTax = order?.prices?.totalPrice?.withoutTax ?? 0;
	const delivery = order?.prices?.deliveryPrice?.withTax ?? 0;
	const deliveryBeforeDiscount = delivery + deliveryDiscount;
	const subtotal = totalPriceWithTax - deliveryBeforeDiscount + deliveryDiscount + totalDiscount;

	const result: OrderSummaryRow[] = [];

	result.push({
		label: translate("subtotal"),
		value: formatPrice(subtotal, currency, locale, true),
	});

	for (const type of discounts) {
		let discountLabel = orderDiscountTypeNameMap[type][locale];

		if (type === OrderDiscountType.Coupon && order?.couponCode) {
			discountLabel += `: ${order.couponCode}`;
		}

		if (discountsByType[type] > 0) {
			result.push({
				label: discountLabel,
				value: formatPrice(-discountsByType[type], currency, locale, true),
			});
		}
	}

	if (showDelivery) {
		const deliveryNote =
			order?.deliveryType !== undefined && order.deliveryAddress !== undefined
				? [deliveryTypeNameMap[order.deliveryType][locale], order.deliveryAddress.street].join(" • ")
				: undefined;

		result.push({
			label: translate("delivery"),
			note: deliveryNote,
			value:
				deliveryBeforeDiscount === 0 || delivery === 0
					? translate("free")
					: formatPrice(deliveryBeforeDiscount, currency, locale, true),
		});

		if (deliveryDiscount > 0 && delivery > 0) {
			result.push({
				label: translate("deliveryDiscount"),
				note: deliveryNote,
				value: formatPrice(-deliveryDiscount, currency, locale, true),
			});
		}
	}

	result.push({
		label: translate("priceIncludingVAT"),
		value: formatPrice(totalPriceWithTax, currency, locale, true),
	});

	result.push({
		label: translate("priceWithoutVAT"),
		value: formatPrice(totalPriceWithoutTax, currency, locale, true),
	});

	if (roundingAdjustment !== 0) {
		result.push({
			label: translate("roundingAdjustment"),
			value: formatPrice(roundingAdjustment, currency, locale, true),
		});
	}

	result.push({
		label: translate("totalWithVat"),
		value: formatPrice(totalPriceWithTax + roundingAdjustment, currency, locale, true),
	});

	return result;
}
